/**
 * Project Cable Service — Projects v2 Phase 3.5 (P3.5.1).
 *
 * CRUD + priceSnapshot capture + BOM aggregation for the
 * `projects/{projectId}/cableRuns/{runId}` sub-collection.
 *
 * Mirrors the structure of project-dwelling.service.ts (P3.1):
 *   - Tenant scoping with canonical 'techery-au' fallback.
 *   - sanitizeCableRun helper for Timestamp → ISO conversion on read.
 *   - runTransaction for any write that touches both the run + parent.
 *   - Activity entries via appendActivityEntry, fire-and-forget after
 *     the primary tx commits (best-effort observability, not a
 *     correctness gate).
 *
 * Built incrementally — this commit ships types + reads + the
 * priceSnapshot helper. Writes (create/update/transition/bulk/archive),
 * cable-type swap, and BOM aggregation land in follow-up commits.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  setDoc,
  Timestamp,
  where,
  writeBatch,
  type DocumentData,
  type QueryConstraint,
  type QuerySnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/firebase';
import { getTenantIdSync, tenantIdReadUnion } from '@/lib/tenant';
import { stripUndefinedDeep } from '@/utils/firestore';
import {
  cableRunAdjustedMeters,
  cableRunQuantity,
} from '@/utils/cable-run-math';
import {
  getActivePartsCatalogItems,
  getPartsCatalogItem,
} from '@/services/parts-catalog.service';
import { appendActivityEntry } from '@/services/project-activity.service';
import { getDwelling } from '@/services/project-dwelling.service';
import {
  getScopeItemMapping,
  resolveScopeItemCableType,
  SCOPE_ITEM_MAP,
} from '@/data/scope-item-to-cable-type-map';
import type { CableRun, CableRunPriceSnapshot } from '@/types/project.types';
import type { PartsCatalogItem, Project } from '@/types/schema';
import { scheduleRecalc } from './project-recalc-trigger';

// ---------------------------------------------------------------------------
// BOM types — exported for the export module + provisioning integration
// ---------------------------------------------------------------------------

export interface CableBOMReels {
  /** Whole 305m reels needed (cheapest per metre — the default order shape). */
  '305m': number;
  /** 100m boxes for any remainder ≥ 100m. */
  '100m': number;
  /** 50m boxes for the final remainder, rounded UP — never under-order. */
  '50m': number;
}

export interface CableBOMGroup {
  catalogItemId: string;
  totalRunCount: number;
  /** Sum across runs of `lengthMeters × wastageFactor × quantity`. */
  totalMetersWithWastage: number;
  reels: CableBOMReels;
  /** Terminations needed = 2 × CABLES in this group (a ×2 run
   *  contributes 4), not 2 × schedule rows. */
  terminationCount: number;
}

export interface CableBOM {
  groups: CableBOMGroup[];
  /** catalogItemId → termination count. Used by Phase 6 to seed
   *  termination provisioning items separately from cable reels. */
  terminationTotals: Map<string, number>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function readTenantId(): string {
  if (typeof window === 'undefined') return 'techery-au';
  return getTenantIdSync();
}

function toISOString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (value && typeof value === 'object' && 'seconds' in (value as Record<string, unknown>)) {
    return new Date((value as { seconds: number }).seconds * 1000).toISOString();
  }
  return new Date().toISOString();
}

/** Coerce any Timestamp-shaped fields on a run doc to ISO strings. */
function sanitizeCableRun(raw: Record<string, unknown>): CableRun {
  const sanitized = { ...raw };
  for (const f of ['createdAt', 'updatedAt', 'testedAt']) {
    if (sanitized[f] && typeof sanitized[f] !== 'string') {
      sanitized[f] = toISOString(sanitized[f]);
    }
  }
  // priceSnapshot.capturedAt is a sub-field — same defensive coercion.
  const ps = sanitized.priceSnapshot as Record<string, unknown> | undefined;
  if (ps && ps.capturedAt && typeof ps.capturedAt !== 'string') {
    ps.capturedAt = toISOString(ps.capturedAt);
  }
  return sanitized as unknown as CableRun;
}

// ---------------------------------------------------------------------------
// captureCableRunPriceSnapshot — pure helper (P3.5.1 §4.7)
// ---------------------------------------------------------------------------

const HARDCODED_WASTAGE_FALLBACK = 1.15;

/**
 * Build an immutable CableRunPriceSnapshot from a catalog item + project-
 * level fallbacks. Returns the snapshot and an array of field names that
 * fell back to defaults — the run records this so the red-badge UI in
 * Phase 4d can flag silent fallbacks.
 *
 * Wastage resolution order: explicit override → catalog's
 * wastageFactorDefault → project's defaultWastageFactor → 1.15 (logs a
 * warning — indicates P0 catalog hygiene regressed and a SKU is missing
 * its wastage seed).
 */
export function captureCableRunPriceSnapshot(
  project: Project,
  catalogItem: PartsCatalogItem,
  wastageOverride?: number
): { snapshot: CableRunPriceSnapshot; usedDefaultsFor: string[] } {
  const usedDefaultsFor: string[] = [];

  const cableCostPrice = catalogItem.costPrice ?? 0;
  const cableSellPrice = catalogItem.sellPrice ?? catalogItem.price ?? 0;

  let terminationCostEach: number;
  if (typeof catalogItem.terminationCostEach === 'number') {
    terminationCostEach = catalogItem.terminationCostEach;
  } else {
    terminationCostEach = project.defaultTerminationCostEach ?? 0;
    usedDefaultsFor.push('terminationCostEach');
  }

  let terminationSellEach: number;
  if (typeof catalogItem.terminationSellEach === 'number') {
    terminationSellEach = catalogItem.terminationSellEach;
  } else {
    terminationSellEach = project.defaultTerminationSellEach ?? 0;
    usedDefaultsFor.push('terminationSellEach');
  }

  let pullLabourMinPerMeter: number;
  if (typeof catalogItem.pullLabourMinPerMeter === 'number') {
    pullLabourMinPerMeter = catalogItem.pullLabourMinPerMeter;
  } else {
    pullLabourMinPerMeter = project.defaultPullLabourMinPerMeter ?? 0;
    usedDefaultsFor.push('pullLabourMinPerMeter');
  }

  let wastageFactor: number;
  if (typeof wastageOverride === 'number') {
    wastageFactor = wastageOverride;
    // Override never counts as "used default" — the user is explicitly
    // setting it.
  } else if (typeof catalogItem.wastageFactorDefault === 'number') {
    wastageFactor = catalogItem.wastageFactorDefault;
  } else if (typeof project.defaultWastageFactor === 'number') {
    wastageFactor = project.defaultWastageFactor;
    usedDefaultsFor.push('wastageFactor');
  } else {
    wastageFactor = HARDCODED_WASTAGE_FALLBACK;
    usedDefaultsFor.push('wastageFactor');
    console.warn(
      `[project-cable] Hard-coded wastage fallback ${HARDCODED_WASTAGE_FALLBACK} hit for catalog item ${catalogItem.id}. P0 hygiene regressed — populate wastageFactorDefault on the SKU.`
    );
  }

  const snapshot: CableRunPriceSnapshot = {
    cableCostPrice,
    cableSellPrice,
    terminationCostEach,
    terminationSellEach,
    pullLabourMinPerMeter,
    wastageFactor,
    capturedAt: new Date().toISOString(),
    catalogItemId: catalogItem.id ?? '',
  };

  return { snapshot, usedDefaultsFor };
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export interface CableRunFilters {
  dwellingId?: string;
  status?: CableRun['status'];
  /** Default false — archived runs hidden from the spreadsheet. */
  includeArchived?: boolean;
}

function buildConstraints(tenantId: string, _filters: CableRunFilters = {}): QueryConstraint[] {
  // Server-side: tenantId + orderBy(runNumber) only. dwellingId AND status
  // both get filtered CLIENT-SIDE in the listener / list helpers below.
  // Why: combining `where(tenantId)` + `where(dwellingId)` +
  // `orderBy(runNumber)` (or +`where(status)` +orderBy) requires
  // composite Firestore indexes that aren't deployed (firestore.indexes.json
  // has no cableRuns entries). Without the index, Firestore returns
  // EMPTY rather than throwing — the dwelling-filtered tab then shows
  // "no runs" even though they exist.
  // The cableRuns sub-collection is small per project (tens to low
  // hundreds), so client-side filter is fine for Phase 3.5 / 5 scale.
  // Phase 9b can revisit + ship indexes when reporting needs them.
  const constraints: QueryConstraint[] = [where('tenantId', '==', tenantId)];
  // Always order by runNumber for stable display + pagination semantics.
  constraints.push(orderBy('runNumber', 'asc'));
  return constraints;
}

function applyClientFilters(runs: CableRun[], filters: CableRunFilters): CableRun[] {
  let out = runs;
  if (filters.dwellingId) {
    out = out.filter((r) => r.dwellingId === filters.dwellingId);
  }
  if (filters.status) {
    out = out.filter((r) => r.status === filters.status);
  }
  return out;
}

export async function getCableRun(
  projectId: string,
  runId: string
): Promise<CableRun | null> {
  const ref = doc(db, 'projects', projectId, 'cableRuns', runId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return sanitizeCableRun({ id: snap.id, ...snap.data() });
}

/** One-shot list with optional filters. */
export async function listCableRuns(
  projectId: string,
  filters: CableRunFilters = {}
): Promise<CableRun[]> {
  const tenantId = readTenantId();
  const q = query(
    collection(db, 'projects', projectId, 'cableRuns'),
    ...buildConstraints(tenantId, filters)
  );
  const snap = await getDocs(q);
  const all = snap.docs.map((d) => sanitizeCableRun({ id: d.id, ...d.data() }));
  // Apply client-side dwelling/status filters first, then archived gate.
  const filtered = applyClientFilters(all, filters);
  return filters.includeArchived ? filtered : filtered.filter((r) => r.status !== 'Archived');
}

/** Real-time listener for the spreadsheet view. */
export function subscribeCableRuns(
  projectId: string,
  cb: (runs: CableRun[]) => void,
  filters: CableRunFilters = {}
): Unsubscribe {
  const tenantId = readTenantId();
  const q = query(
    collection(db, 'projects', projectId, 'cableRuns'),
    ...buildConstraints(tenantId, filters)
  );
  return onSnapshot(
    q,
    (snap: QuerySnapshot<DocumentData>) => {
      const all = snap.docs.map((d) => sanitizeCableRun({ id: d.id, ...d.data() }));
      const filtered = applyClientFilters(all, filters);
      cb(filters.includeArchived ? filtered : filtered.filter((r) => r.status !== 'Archived'));
    },
    (err) => {
      console.warn(`[project-cable] subscribe error on project ${projectId}:`, err);
      cb([]);
    }
  );
}

// ---------------------------------------------------------------------------
// Imports needed for writes — added by 3/N append
// (the SDK + types we already imported above are sufficient)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Internal helpers for writes
// ---------------------------------------------------------------------------

function readActor(): { id: string; name: string } {
  if (typeof window === 'undefined') return { id: '', name: 'system' };
  return {
    id: localStorage.getItem('user_id') ?? '',
    name: localStorage.getItem('user_name') ?? 'Unknown',
  };
}

function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj) as [keyof T, T[keyof T]][]) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

/** Allowed forward transitions per master plan §4.6.
 *  Failed branches off Tested only. Archived is reachable via
 *  archiveCableRun (one-way unless restoreCableRun called). */
const STATUS_TRANSITIONS: Record<CableRun['status'], CableRun['status'][]> = {
  Planned: ['Pulled', 'Archived'],
  Pulled: ['Terminated', 'Archived'],
  Terminated: ['Tested', 'Archived'],
  Tested: ['Certified', 'Failed', 'Archived'],
  Certified: ['Archived'],
  Failed: ['Tested', 'Archived'], // re-test allowed after a fail
  Archived: [], // exits via restoreCableRun, not a transition
};

function isValidTransition(
  from: CableRun['status'],
  to: CableRun['status']
): boolean {
  if (from === to) return false;
  return STATUS_TRANSITIONS[from]?.includes(to) ?? false;
}

async function loadProject(projectId: string): Promise<Project> {
  const ref = doc(db, 'projects', projectId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error(`Project ${projectId} not found`);
  return { id: snap.id, ...snap.data() } as Project;
}

// ---------------------------------------------------------------------------
// createCableRun — atomic + first-run-per-dwelling activity entry
// ---------------------------------------------------------------------------

export async function createCableRun(
  projectId: string,
  data: Omit<CableRun, 'id' | 'projectId' | 'runNumber' | 'priceSnapshot' | 'createdAt' | 'updatedAt'>,
  actor?: { id: string; name: string }
): Promise<string> {
  const tenantId = readTenantId();
  const runId = crypto.randomUUID();
  const now = new Date().toISOString();
  const runActor = actor ?? readActor();

  // Resolve catalog item + project (outside the tx — both reads are safe to
  // do upfront and the tx itself only touches the run + the run-number
  // counter on the parent).
  const project = await loadProject(projectId);
  if ((project.tenantId ?? 'techery-au') !== tenantId) {
    throw new Error(
      `Tenant mismatch on project ${projectId} — caller="${tenantId}"`
    );
  }

  let catalogItem: PartsCatalogItem | null = null;
  if (data.cableCatalogItemId) {
    catalogItem = await getPartsCatalogItem(data.cableCatalogItemId);
  }

  // Capture the priceSnapshot (immutable for the run's lifetime). If no
  // catalog item is linked, fall back to a zero-priced snapshot so the
  // shape stays valid; cost rollups will reflect $0 until the user
  // assigns a cable type.
  const { snapshot, usedDefaultsFor } = catalogItem
    ? captureCableRunPriceSnapshot(project, catalogItem)
    : {
        snapshot: {
          cableCostPrice: 0,
          cableSellPrice: 0,
          terminationCostEach: 0,
          terminationSellEach: 0,
          pullLabourMinPerMeter: 0,
          wastageFactor: 1,
          capturedAt: now,
          catalogItemId: '',
        } as CableRunPriceSnapshot,
        usedDefaultsFor: [] as string[],
      };

  const runRef = doc(db, 'projects', projectId, 'cableRuns', runId);
  const projectRef = doc(db, 'projects', projectId);

  // Pre-tx read of existing runs: only used to (a) seed the persistent
  // counter the first time a project has no `cableRunCounter` yet, and
  // (b) drive the first-run-per-dwelling activity coalescing below.
  const existingSnap = await getDocs(
    query(
      collection(db, 'projects', projectId, 'cableRuns'),
      where('tenantId', '==', tenantId)
    )
  );
  const maxRunNumber = existingSnap.docs
    .map((d) => (d.data().runNumber as number | undefined) ?? 0)
    .reduce((a, b) => Math.max(a, b), 0);

  // runNumber is assigned from a per-project counter read+written INSIDE the
  // transaction. Two concurrent creates both touch projectRef, so Firestore
  // serialises them (the loser retries against the freshly-incremented
  // counter) — no duplicate run numbers. The `?? maxRunNumber` only applies
  // the first time a counter-less project gets a run; after that the
  // persisted counter wins. (Replaces the old race-tolerant pre-tx max read.)
  await runTransaction(db, async (tx) => {
    const projSnap = await tx.get(projectRef);
    const priorCounter =
      (projSnap.data()?.cableRunCounter as number | undefined) ?? maxRunNumber;
    const nextRunNumber = priorCounter + 1;

    const fullRun: CableRun & { tenantId: string } = {
      ...data,
      id: runId,
      projectId,
      runNumber: nextRunNumber,
      priceSnapshot: snapshot,
      usedDefaultsFor,
      tenantId,
      createdAt: now,
      updatedAt: now,
    } as CableRun & { tenantId: string };

    tx.set(runRef, stripUndefined(fullRun as unknown as Record<string, unknown>));
    tx.update(projectRef, { cableRunCounter: nextRunNumber });
  });

  // First-run-per-dwelling activity entry: only fire if no prior run for
  // this dwelling already exists. Coalesces noise on multi-run dwellings.
  if (data.dwellingId) {
    const priorRuns = existingSnap.docs.filter(
      (d) => (d.data().dwellingId as string | undefined) === data.dwellingId
    );
    if (priorRuns.length === 0) {
      await appendActivityEntry(projectId, {
        type: 'cable_run_added',
        actor: runActor.id,
        actorName: runActor.name,
        action: `Cable runs started${data.label ? ` — ${data.label}` : ''}`,
        details: usedDefaultsFor.length
          ? `Defaulted: ${usedDefaultsFor.join(', ')}`
          : undefined,
        dwellingId: data.dwellingId,
      }).catch((err) => {
        console.warn(`[project-cable] activity entry failed for ${runId}:`, err);
      });
    }
  }

  // P9a.3 — a new cable run adds planned cost.
  scheduleRecalc(projectId);

  return runId;
}

// ---------------------------------------------------------------------------
// updateCableRun — partial update; priceSnapshot stays immutable
// ---------------------------------------------------------------------------

export async function updateCableRun(
  projectId: string,
  runId: string,
  updates: Partial<Omit<CableRun, 'id' | 'projectId' | 'priceSnapshot' | 'createdAt'>>
): Promise<void> {
  const tenantId = readTenantId();
  const ref = doc(db, 'projects', projectId, 'cableRuns', runId);
  const cleaned = stripUndefined({
    ...updates,
    updatedAt: new Date().toISOString(),
  } as Record<string, unknown>);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new Error(`Cable run ${runId} not found on project ${projectId}`);
    }
    // Audit fix: defence-in-depth tenant check. The bulk path validates
    // already; single-row writes need the same guard so a malformed
    // call site can't write across tenants under the baseline auth-only
    // Firestore rules. Match the same string the bulk path produces.
    const docTenant = (snap.data().tenantId as string | undefined) ?? 'techery-au';
    if (docTenant !== tenantId) {
      throw new Error(
        `Tenant mismatch on run ${runId} — caller="${tenantId}" doc="${docTenant}"`
      );
    }
    tx.update(ref, cleaned);
  });

  // P9a.3 — length / cable-type edits change the run's planned cost.
  scheduleRecalc(projectId);
}

// ---------------------------------------------------------------------------
// transitionCableRunStatus — single status-transition entry point
// ---------------------------------------------------------------------------

export async function transitionCableRunStatus(
  projectId: string,
  runId: string,
  newStatus: CableRun['status'],
  actor?: { id: string; name: string },
  metadata?: { testedBy?: string; testResult?: 'pass' | 'fail' }
): Promise<void> {
  const tenantId = readTenantId();
  const transitionActor = actor ?? readActor();
  const ref = doc(db, 'projects', projectId, 'cableRuns', runId);

  let label = '';
  let prevStatus: CableRun['status'] = 'Planned';

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new Error(`Cable run ${runId} not found`);
    }
    const data = snap.data() as Record<string, unknown>;
    // Audit fix: tenant guard, same as updateCableRun.
    const docTenant = (data.tenantId as string | undefined) ?? 'techery-au';
    if (docTenant !== tenantId) {
      throw new Error(
        `Tenant mismatch on run ${runId} — caller="${tenantId}" doc="${docTenant}"`
      );
    }
    prevStatus = data.status as CableRun['status'];
    label = (data.label as string | undefined) ?? '';

    if (!isValidTransition(prevStatus, newStatus)) {
      throw new Error(
        `Invalid transition for run "${label}": ${prevStatus} → ${newStatus}. Allowed: ${STATUS_TRANSITIONS[prevStatus].join(', ') || '(none)'}`
      );
    }

    const update: Record<string, unknown> = {
      status: newStatus,
      updatedAt: new Date().toISOString(),
    };
    if (newStatus === 'Tested' && metadata?.testedBy) {
      update.testedBy = metadata.testedBy;
      update.testedAt = new Date().toISOString();
      if (metadata.testResult) update.testResult = metadata.testResult;
    }
    tx.update(ref, update);
  });

  await appendActivityEntry(projectId, {
    type: 'cable_run_status_changed',
    actor: transitionActor.id,
    actorName: transitionActor.name,
    action: `Run ${label} → ${newStatus}`,
    details: `Previous: ${prevStatus}`,
  }).catch((err) => {
    console.warn(`[project-cable] activity entry failed for ${runId}:`, err);
  });

  // P9a.3 — a transition to/from 'Archived' moves the run in/out of the
  // cost sum; other status changes are cheap no-op recalcs (debounced).
  scheduleRecalc(projectId);
}

// ---------------------------------------------------------------------------
// changeCableType — re-snapshot pricing (pre-contract only)
// ---------------------------------------------------------------------------

const CONTRACTED_DEAL_STAGES = new Set([
  'QUOTING',
  'PROPOSAL_SENT',
  'WON',
  'PROVISIONING',
  'SCHEDULING',
  'REVIEW',
  'BILLING',
  'COMPLETED',
]);

export async function changeCableType(
  projectId: string,
  runId: string,
  newCatalogItemId: string,
  actor?: { id: string; name: string }
): Promise<void> {
  const project = await loadProject(projectId);
  // Master plan §4.7: re-snapshot only allowed pre-contract. Once a project
  // is contracted, only a variation can change pricing. Project.stage isn't
  // an explicit field on the type — projects v2 inherits the deal-style
  // stage on Project.stages[]; the contract gate uses primaryDealId's
  // stage. For Phase 3.5 we keep a permissive check: if a primaryDealId is
  // linked AND we can verify the deal stage server-side, block; otherwise
  // allow. Deal-side enforcement (Phase 7) is the real gate.
  const primaryDealId = (project as Project & { primaryDealId?: string })
    .primaryDealId;
  if (primaryDealId) {
    try {
      const dealSnap = await getDoc(doc(db, 'deals', primaryDealId));
      if (dealSnap.exists()) {
        const stage = (dealSnap.data().stage as string | undefined) ?? '';
        if (CONTRACTED_DEAL_STAGES.has(stage)) {
          throw new Error(
            `Cannot change cable type — deal ${primaryDealId} is at ${stage}. Use a variation instead.`
          );
        }
      }
    } catch (err) {
      // Network errors reading the deal shouldn't gate the swap; only
      // explicit contracted-stage rejections do. Re-throw if it's our own
      // thrown error.
      if (err instanceof Error && err.message.startsWith('Cannot change cable type')) {
        throw err;
      }
    }
  }

  const catalogItem = await getPartsCatalogItem(newCatalogItemId);
  if (!catalogItem) {
    throw new Error(`Catalog item ${newCatalogItemId} not found`);
  }
  const { snapshot, usedDefaultsFor } = captureCableRunPriceSnapshot(
    project,
    catalogItem
  );

  const ref = doc(db, 'projects', projectId, 'cableRuns', runId);
  let label = '';
  let prevCatalogItemId = '';

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new Error(`Cable run ${runId} not found`);
    }
    const data = snap.data() as Record<string, unknown>;
    // Audit fix: tenant guard, same as updateCableRun + transitionCableRunStatus.
    // The earlier project-level check (line 334) catches mismatch on the parent
    // project; this catches a malformed call where projectId belongs to the
    // tenant but runId was crafted from another tenant's project.
    const docTenant = (data.tenantId as string | undefined) ?? 'techery-au';
    const callerTenant = readTenantId();
    if (docTenant !== callerTenant) {
      throw new Error(
        `Tenant mismatch on run ${runId} — caller="${callerTenant}" doc="${docTenant}"`
      );
    }
    label = (data.label as string | undefined) ?? '';
    prevCatalogItemId = (data.cableCatalogItemId as string | undefined) ?? '';

    tx.update(ref, {
      cableCatalogItemId: newCatalogItemId,
      priceSnapshot: snapshot,
      usedDefaultsFor,
      updatedAt: new Date().toISOString(),
    });
  });

  const swapActor = actor ?? readActor();
  await appendActivityEntry(projectId, {
    type: 'cable_run_status_changed',
    actor: swapActor.id,
    actorName: swapActor.name,
    action: `Changed cable type on ${label} — price re-snapshot`,
    details: `From ${prevCatalogItemId || '(none)'} → ${newCatalogItemId}${
      usedDefaultsFor.length ? ` · defaulted: ${usedDefaultsFor.join(', ')}` : ''
    }`,
  }).catch((err) => {
    console.warn(`[project-cable] activity entry failed for ${runId}:`, err);
  });
}

// ---------------------------------------------------------------------------
// bulkUpdateStatus — single aggregate activity entry, validate then commit
// ---------------------------------------------------------------------------

export async function bulkUpdateStatus(
  projectId: string,
  runIds: string[],
  newStatus: CableRun['status'],
  actor?: { id: string; name: string }
): Promise<void> {
  if (runIds.length === 0) return;
  const tenantId = readTenantId();
  const bulkActor = actor ?? readActor();

  // Validate all transitions BEFORE writing — reject the whole batch on
  // any invalid one to keep semantics atomic from the user's perspective.
  const previousStatuses: Record<string, CableRun['status']> = {};
  const invalid: string[] = [];

  await Promise.all(
    runIds.map(async (id) => {
      const snap = await getDoc(doc(db, 'projects', projectId, 'cableRuns', id));
      if (!snap.exists()) {
        invalid.push(`${id} (not found)`);
        return;
      }
      const data = snap.data();
      if ((data.tenantId as string | undefined) !== tenantId) {
        invalid.push(`${id} (tenant mismatch)`);
        return;
      }
      const status = data.status as CableRun['status'];
      previousStatuses[id] = status;
      if (!isValidTransition(status, newStatus)) {
        invalid.push(`${id} (${status} → ${newStatus})`);
      }
    })
  );

  if (invalid.length > 0) {
    throw new Error(
      `Bulk status change rejected — invalid transitions:\n  ${invalid.join('\n  ')}`
    );
  }

  // Chunk into ≤ 19-op batches. REQUIRED by the rules lock: projects/*/cableRuns
  // writes may be authorised by a parent-lookup get() per op, and a batched write
  // is capped at 20 rules access calls (belt-and-braces alongside the stamped-doc
  // short-circuit). Do not raise this back toward Firestore's 500-op cap.
  const BATCH_LIMIT = 19;
  for (let i = 0; i < runIds.length; i += BATCH_LIMIT) {
    const chunk = runIds.slice(i, i + BATCH_LIMIT);
    const batch = writeBatch(db);
    const now = new Date().toISOString();
    for (const id of chunk) {
      batch.update(doc(db, 'projects', projectId, 'cableRuns', id), {
        status: newStatus,
        updatedAt: now,
      });
    }
    await batch.commit();
  }

  // Single aggregate activity entry — keeps the timeline readable per
  // P3.5.1 spec.
  await appendActivityEntry(projectId, {
    type: 'cable_run_status_changed',
    actor: bulkActor.id,
    actorName: bulkActor.name,
    action: `Bulk status change: ${runIds.length} runs → ${newStatus}`,
    details: JSON.stringify({ runIds, newStatus, previousStatuses }),
  }).catch((err) => {
    console.warn('[project-cable] aggregate activity entry failed:', err);
  });

  // P9a.3 — bulk archive/unarchive moves runs in/out of the cost sum.
  scheduleRecalc(projectId);
}

// ---------------------------------------------------------------------------
// P5.6 — Job-completion → cable-run status propagation
// ---------------------------------------------------------------------------

/** Result of a job-completion propagation pass. The job-side-effects
 *  caller stamps this onto `lastSideEffectError` only when
 *  `failed > 0`, so PMs can see the cable propagation succeeded
 *  cleanly even if other side effects fail. */
export interface PropagateJobCompletionResult {
  /** Runs that were certified by this pass (status was Tested,
   *  now Certified). */
  certified: number;
  /** Runs at the stage that were ineligible — already at Certified
   *  or Archived, OR not yet at Tested (the lifecycle requires
   *  Tested → Certified, not arbitrary jumps). PM-visible via the
   *  return value; not an error condition. */
  skipped: number;
  /** Runs the bulk call rejected — usually a tenant-isolation
   *  mismatch (defensive; shouldn't happen in practice). */
  failed: number;
}

/**
 * P5.6: when a commissioning job transitions to COMPLETED, every
 * cable run assigned to its `projectStageId` and sitting at status
 * `Tested` advances to `Certified` in one bulk batch. Runs at any
 * other status are skipped — the lifecycle doesn't allow direct
 * jumps from Planned/Pulled/Terminated to Certified, and skipping
 * a partial-progress run keeps the PM honest about what was
 * actually field-tested.
 *
 * Fire-and-forget contract from job-side-effects — failures are
 * caught at the caller and surfaced via `lastSideEffectError`.
 * This helper does NOT throw on its own: it pre-filters runs to
 * eligible-only so `bulkUpdateStatus`'s lifecycle validator can't
 * reject the whole batch over a single Planned outlier. The "I
 * tried to certify a run that's still at Planned" case is
 * captured in the result's `skipped` count for PM visibility.
 *
 * Re-runnable: the second call sees only ineligible runs (already
 * Certified) and is a no-op.
 */
export async function propagateJobCompletionToCableRuns(
  projectId: string,
  projectStageId: string,
  actor?: { id: string; name: string }
): Promise<PropagateJobCompletionResult> {
  // Fetch runs for this project; client-side filter by stageId.
  // listCableRuns doesn't accept a stage filter today and adding
  // one would need a composite Firestore index (tenantId × stageId
  // × deletedAt). Per the master plan's <200-runs-per-project
  // target this scan is cheap.
  const all = await listCableRuns(projectId, {});
  const stageRuns = all.filter((r) => r.stageId === projectStageId);
  if (stageRuns.length === 0) {
    return { certified: 0, skipped: 0, failed: 0 };
  }
  // Eligibility: only Tested → Certified is allowed by the
  // lifecycle (STATUS_TRANSITIONS above). Runs at other statuses
  // are skipped — they need the per-checkpoint propagation path
  // (P5.6 stretch — see InstallChecklistModule) or manual PM
  // intervention via the side panel.
  const eligible = stageRuns.filter((r) => r.status === 'Tested');
  const skipped = stageRuns.length - eligible.length;
  if (eligible.length === 0) {
    return { certified: 0, skipped, failed: 0 };
  }
  try {
    await bulkUpdateStatus(
      projectId,
      eligible.map((r) => r.id),
      'Certified',
      actor
    );
    return { certified: eligible.length, skipped, failed: 0 };
  } catch (err) {
    console.warn('[project-cable] propagateJobCompletion bulk update failed:', err);
    return { certified: 0, skipped, failed: eligible.length };
  }
}

// ---------------------------------------------------------------------------
// Soft-delete: archive / restore
// ---------------------------------------------------------------------------

export async function archiveCableRun(projectId: string, runId: string): Promise<void> {
  const tenantId = readTenantId();
  const ref = doc(db, 'projects', projectId, 'cableRuns', runId);
  const actor = readActor();
  let label = '';
  let prev: CableRun['status'] = 'Planned';

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error(`Cable run ${runId} not found`);
    const data = snap.data() as Record<string, unknown>;
    // Audit fix: tenant guard, same as updateCableRun.
    const docTenant = (data.tenantId as string | undefined) ?? 'techery-au';
    if (docTenant !== tenantId) {
      throw new Error(
        `Tenant mismatch on run ${runId} — caller="${tenantId}" doc="${docTenant}"`
      );
    }
    if (data.status === 'Archived') return; // idempotent
    label = (data.label as string | undefined) ?? '';
    prev = data.status as CableRun['status'];
    tx.update(ref, { status: 'Archived', updatedAt: new Date().toISOString() });
  });

  // Audit fix: previously gated activity on `if (label)` which
  // suppressed the entry whenever the run was archived before the user
  // typed a label. We still need a sentinel for the "already archived"
  // idempotent early-return — use `prev` for that (default 'Planned'
  // means the tx never ran the real path). Empty-label runs now show
  // up in the timeline as "Archived run #{runNumber}" via the action
  // string fallback.
  if (prev !== 'Planned' || label) {
    const display = label || `#${runId.slice(0, 8)}`;
    await appendActivityEntry(projectId, {
      type: 'cable_run_status_changed',
      actor: actor.id,
      actorName: actor.name,
      action: `Archived run ${display}`,
      details: `Previous: ${prev}`,
    }).catch((err) => {
      console.warn(`[project-cable] activity entry failed for ${runId}:`, err);
    });
  }

  // P9a.3 — archiving removes the run from the cost sum.
  scheduleRecalc(projectId);
}

export async function restoreCableRun(projectId: string, runId: string): Promise<void> {
  const tenantId = readTenantId();
  const ref = doc(db, 'projects', projectId, 'cableRuns', runId);
  const actor = readActor();
  let label = '';
  let restored = false;

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error(`Cable run ${runId} not found`);
    const data = snap.data() as Record<string, unknown>;
    // Audit fix: tenant guard, same as updateCableRun.
    const docTenant = (data.tenantId as string | undefined) ?? 'techery-au';
    if (docTenant !== tenantId) {
      throw new Error(
        `Tenant mismatch on run ${runId} — caller="${tenantId}" doc="${docTenant}"`
      );
    }
    if (data.status !== 'Archived') return; // idempotent
    label = (data.label as string | undefined) ?? '';
    restored = true;
    tx.update(ref, { status: 'Planned', updatedAt: new Date().toISOString() });
  });

  // Audit fix: same as archiveCableRun — drop the `if (label)` gate
  // that suppressed the timeline entry on empty-label runs. Use a
  // dedicated `restored` flag for the idempotent-skip case.
  if (restored) {
    const display = label || `#${runId.slice(0, 8)}`;
    await appendActivityEntry(projectId, {
      type: 'cable_run_status_changed',
      actor: actor.id,
      actorName: actor.name,
      action: `Restored run ${display}`,
      details: 'Archived → Planned',
    }).catch((err) => {
      console.warn(`[project-cable] activity entry failed for ${runId}:`, err);
    });
  }

  // P9a.3 — restoring adds the run back into the cost sum.
  scheduleRecalc(projectId);
}

// ---------------------------------------------------------------------------
// aggregateCableBOM — pure, group by catalog item id
// ---------------------------------------------------------------------------

/**
 * Per master plan §4.6: group by catalog item, sum length × wastage ×
 * quantity, convert to reel sizes (305m / 100m / 50m). 50m rounds UP —
 * never under-order.
 *
 * Reel splitting algorithm:
 *   - Take floor(total / 305) full 305m reels.
 *   - Take floor(remainder / 100) for 100m boxes.
 *   - Take ceil(remainder / 50) for the final 50m boxes.
 *   - If total is 0, all three counts are 0.
 *
 * Termination counts = 2 × runs per catalog group (head + tail). Phase 6
 * uses these to seed termination ProvisioningItems separately from cable
 * reels.
 */
export function aggregateCableBOM(runs: CableRun[]): CableBOM {
  const byItem = new Map<string, { meters: number; runCount: number }>();
  for (const run of runs) {
    if (run.status === 'Archived') continue;
    const itemId = run.priceSnapshot?.catalogItemId ?? '';
    if (!itemId) continue; // unassigned cable type — skip
    // cableRunAdjustedMeters folds in the run's `quantity` — a "×2 Cat6
    // to the TV wall" row must order two cables' worth of reel, not one,
    // or the procurement order comes up short on site.
    const adjusted = cableRunAdjustedMeters(run);
    const existing = byItem.get(itemId) ?? { meters: 0, runCount: 0 };
    existing.meters += adjusted;
    // Count each physical cable, not each schedule row — terminations
    // below are 2 per cable.
    existing.runCount += cableRunQuantity(run);
    byItem.set(itemId, existing);
  }

  const groups: CableBOMGroup[] = [];
  const terminationTotals = new Map<string, number>();
  for (const [itemId, { meters, runCount }] of byItem) {
    const reels = splitToReels(meters);
    const terminationCount = runCount * 2;
    groups.push({
      catalogItemId: itemId,
      totalRunCount: runCount,
      totalMetersWithWastage: Math.round(meters * 100) / 100,
      reels,
      terminationCount,
    });
    terminationTotals.set(itemId, terminationCount);
  }

  // Stable sort by run count desc — biggest groups first.
  groups.sort((a, b) => b.totalRunCount - a.totalRunCount);

  return { groups, terminationTotals };
}

function splitToReels(totalMeters: number): CableBOMReels {
  if (totalMeters <= 0) return { '305m': 0, '100m': 0, '50m': 0 };
  const r305 = Math.floor(totalMeters / 305);
  const remAfter305 = totalMeters - r305 * 305;
  const r100 = Math.floor(remAfter305 / 100);
  const remAfter100 = remAfter305 - r100 * 100;
  const r50 = remAfter100 > 0 ? Math.ceil(remAfter100 / 50) : 0;
  return { '305m': r305, '100m': r100, '50m': r50 };
}

// ---------------------------------------------------------------------------
// P5.7 — BOM → ProvisioningItem seeding
// ---------------------------------------------------------------------------

/** Result of a `seedProvisioningFromCableBOM` pass. The UI surfaces
 *  the counts in a toast so the PM can see what landed without
 *  navigating to /home/provisioning. */
export interface SeedProvisioningResult {
  /** New provisioning items created. */
  created: number;
  /** Existing items skipped because (projectId, waveNumber, catalogItemId)
   *  + classification already had a row. Idempotency proof. */
  skipped: number;
  /** Items that failed to create (Firestore reject / tenant guard /
   *  catalog lookup miss). Surfaces in the toast so the PM knows the
   *  partial result. */
  failed: number;
}

interface SeedProvisioningOptions {
  /** Provisioning wave to seed into. Phase 6 ships the wave-management
   *  UI; P5.7 defaults to 1 at the call-site. */
  waveNumber: number;
  /** Optional dwelling — when present, every seeded item carries it
   *  as `projectDwellingId` so Phase 6's Wave Board can group by
   *  property. */
  dwellingId?: string;
}

/**
 * P5.7: take an aggregated `CableBOM` and seed `provisioning_items`
 * documents for each group + each group's terminations. Idempotent:
 * pre-flight reads existing project-scoped items in the target wave
 * to skip duplicates. Re-running is a no-op.
 *
 * Two items per cable type:
 *   1. The reel material — `quantity = totalMetersWithWastage` (round
 *      up), `metadata.unitOfMeasure = 'm'`, reels breakdown stashed
 *      in `metadata.reels`.
 *   2. The terminations — `quantity = group.terminationCount`,
 *      `metadata.unitOfMeasure = 'ea'`, suffix " — Terminations" on
 *      the itemName.
 *
 * Both write with `sourceType: 'deal'` (the schema's only option
 * today) + `dealId: ''` per the existing "fault-sourced items use
 * empty string" convention. Future Phase 6 may introduce a
 * `sourceType: 'project'` variant; for now the empty-dealId path is
 * the documented escape.
 *
 * The activity entry is one aggregate write per call (per the
 * `cable_run_imported` pattern) so the project timeline stays
 * readable when a PM seeds 50 cable types at once.
 */
export async function seedProvisioningFromCableBOM(
  projectId: string,
  bom: CableBOM,
  options: SeedProvisioningOptions,
  actor?: { id: string; name: string }
): Promise<SeedProvisioningResult> {
  if (bom.groups.length === 0) {
    return { created: 0, skipped: 0, failed: 0 };
  }
  const tenantId = readTenantId();
  const seedActor = actor ?? readActor();
  const now = new Date().toISOString();

  // Pre-flight: load every existing provisioning item for this
  // project + wave so we can dedupe by (catalogItemId, classification)
  // without N round-trips. Idempotency check uses metadata since
  // ProvisioningItem doesn't carry `partsCatalogItemId` as a
  // first-class field — Phase 6 may promote it.
  // tenantId filter is MANDATORY here: provisioning_items is a strict-rule
  // tenant-isolated collection — Firestore rejects a list query without a
  // tenantId constraint wholesale (CLAUDE.md "STRICT-RULE COLLECTIONS").
  // Without it the whole seed aborts under deployed rules. The matching
  // composite index (tenantId, projectId, waveNumber, createdAt) exists.
  const provQ = query(
    collection(db, 'provisioning_items'),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    where('projectId', '==', projectId),
    where('waveNumber', '==', options.waveNumber)
  );
  let existing: Array<{ catalogItemId?: string; isTermination?: boolean }> = [];
  try {
    const snap = await getDocs(provQ);
    existing = snap.docs.map((d) => {
      const data = d.data() as { metadata?: { partsCatalogItemId?: string; isTermination?: boolean } };
      return {
        catalogItemId: data.metadata?.partsCatalogItemId,
        isTermination: !!data.metadata?.isTermination,
      };
    });
  } catch (err) {
    // Can't dedupe without the existing list — bail rather than risk
    // duplicates. Caller surfaces.
    console.warn('[project-cable] seedProvisioning pre-flight read failed:', err);
    throw new Error('Could not read existing provisioning items — seed aborted');
  }
  const existingKeys = new Set(
    existing
      .filter((e) => e.catalogItemId)
      .map((e) => `${e.catalogItemId}∷${e.isTermination ? 'term' : 'mat'}`)
  );

  // Catalog name lookup — fire in parallel, cached in a Map so we
  // don't re-fetch for the matching termination row.
  const nameCache = new Map<string, string>();
  await Promise.all(
    bom.groups.map(async (g) => {
      if (nameCache.has(g.catalogItemId)) return;
      try {
        const item = await getPartsCatalogItem(g.catalogItemId);
        nameCache.set(g.catalogItemId, item?.name ?? `Cable ${g.catalogItemId.slice(0, 6)}`);
      } catch {
        nameCache.set(g.catalogItemId, `Cable ${g.catalogItemId.slice(0, 6)}`);
      }
    })
  );

  let created = 0;
  let skipped = 0;
  let failed = 0;

  // Write each group sequentially — Firestore handles the load fine,
  // and we want a per-row error to skip that row not the whole batch.
  // No writeBatch here because the addDoc path captures auto-IDs that
  // the batch API can't surface as cleanly per-doc.
  for (const group of bom.groups) {
    const cableName = nameCache.get(group.catalogItemId) ?? 'Cable';

    // Material row
    const matKey = `${group.catalogItemId}∷mat`;
    if (existingKeys.has(matKey)) {
      skipped += 1;
    } else {
      try {
        const quantityMetres = Math.ceil(group.totalMetersWithWastage);
        await addDocViaSetDoc({
          tenantId,
          dealId: '',
          sourceType: 'deal',
          itemName: cableName,
          quantity: quantityMetres,
          status: 'NOT_ORDERED',
          provisioningCategory: 'hardware',
          projectId,
          projectDwellingId: options.dwellingId,
          waveNumber: options.waveNumber,
          metadata: {
            partsCatalogItemId: group.catalogItemId,
            unitOfMeasure: 'm',
            reels: group.reels,
            sourceBom: true,
            isTermination: false,
          },
          createdAt: now,
          updatedAt: now,
        });
        created += 1;
      } catch (err) {
        console.warn('[project-cable] seedProvisioning material create failed:', err);
        failed += 1;
      }
    }

    // Terminations row
    const termKey = `${group.catalogItemId}∷term`;
    if (existingKeys.has(termKey)) {
      skipped += 1;
    } else if (group.terminationCount > 0) {
      try {
        await addDocViaSetDoc({
          tenantId,
          dealId: '',
          sourceType: 'deal',
          itemName: `${cableName} — Terminations`,
          quantity: group.terminationCount,
          status: 'NOT_ORDERED',
          provisioningCategory: 'hardware',
          projectId,
          projectDwellingId: options.dwellingId,
          waveNumber: options.waveNumber,
          metadata: {
            partsCatalogItemId: group.catalogItemId,
            unitOfMeasure: 'ea',
            sourceBom: true,
            isTermination: true,
          },
          createdAt: now,
          updatedAt: now,
        });
        created += 1;
      } catch (err) {
        console.warn('[project-cable] seedProvisioning termination create failed:', err);
        failed += 1;
      }
    }
  }

  // Single aggregate activity entry — keeps timeline readable per
  // the cable_run_imported precedent. Skip when nothing was created
  // (re-runs after a clean dedupe shouldn't pollute the log).
  if (created > 0) {
    await appendActivityEntry(projectId, {
      type: 'provisioning_seeded_from_bom',
      actor: seedActor.id,
      actorName: seedActor.name,
      action: `Seeded ${created} provisioning items from cable BOM (Wave ${options.waveNumber})`,
      details: JSON.stringify({
        created,
        skipped,
        failed,
        waveNumber: options.waveNumber,
        groupCount: bom.groups.length,
      }),
    }).catch((err) => {
      console.warn('[project-cable] seedProvisioning activity entry failed:', err);
    });
  }

  return { created, skipped, failed };
}

/** Local addDoc shim — provisioning.service.ts owns the canonical
 *  createProvisioningItem path but its signature is deal-scoped
 *  (requires a Deal lookup). For project-sourced items we go
 *  directly via setDoc with an auto-generated id so we don't get
 *  blocked on the deal-resolution side effects. The data shape
 *  matches the v2 ProvisioningItem interface in
 *  theo-workflow.types.ts. Tenant-isolation rules require
 *  `tenantId` on the write — included by the caller.
 *
 *  stripUndefinedDeep is MANDATORY: Firestore is initialized without
 *  ignoreUndefinedProperties (see src/firebase.ts), so any nested
 *  `undefined` (e.g. projectDwellingId when no dwelling filter is set)
 *  rejects the whole write. */
async function addDocViaSetDoc(data: Record<string, unknown>): Promise<void> {
  const ref = doc(collection(db, 'provisioning_items'));
  await setDoc(ref, stripUndefinedDeep(data));
}

// ---------------------------------------------------------------------------
// Cable-type catalog resolution — Projects v2 Phase 5 (P5.2)
// ---------------------------------------------------------------------------

/**
 * Fuzzy-match a cable-type name (e.g. "Cat6A", "RG6", "Speaker 14/2")
 * against the active parts catalog. Returns the highest-usage cable-
 * like item whose name OR sku contains the token (case-insensitive,
 * word-boundary preferred over substring). Returns null when no
 * cable-like item matches — the caller should fall back to the
 * full cable-type dropdown.
 *
 * Used by P5.2's auto-from-line creation path to pre-fill the cable
 * type dropdown in CableRunCreatePromptModal from a symbol's
 * `defaultCableType` hint.
 *
 * "Cable-like" filter mirrors the rule used by CableScheduleRow's
 * inline cable-type dropdown (P3.5.3 audit fix): only items with
 * `wastageFactorDefault` or `pullLabourMinPerMeter` populated, OR a
 * category that contains "cable". Keeps APs / cameras / non-cable
 * gear out of the cable-type pool.
 */
export async function findCableCatalogByTypeName(
  typeName: string
): Promise<PartsCatalogItem | null> {
  const trimmed = typeName.trim().toLowerCase();
  if (!trimmed) return null;
  const all = await getActivePartsCatalogItems();
  const cableLike = all.filter(
    (p) =>
      typeof p.wastageFactorDefault === 'number' ||
      typeof p.pullLabourMinPerMeter === 'number' ||
      (p.category ?? '').toLowerCase().includes('cable')
  );
  // Word-boundary regex first — matches "Cat6A" exactly in "Cat6A
  // U/FTP Reel 305m" without also matching unrelated SKUs that
  // happen to contain the substring "cat".
  const wordRe = new RegExp(`\\b${trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
  const wordHits = cableLike.filter(
    (p) =>
      wordRe.test(p.name ?? '') ||
      wordRe.test(p.sku ?? '') ||
      wordRe.test(p.brand ?? '')
  );
  const candidates = wordHits.length > 0
    ? wordHits
    : cableLike.filter(
        (p) =>
          (p.name ?? '').toLowerCase().includes(trimmed) ||
          (p.sku ?? '').toLowerCase().includes(trimmed)
      );
  if (candidates.length === 0) return null;
  // Stable sort by usageCount desc — pick the team's go-to SKU for
  // this cable type. Ties broken by name asc for determinism.
  candidates.sort((a, b) => {
    const ua = (a.usageCount ?? 0);
    const ub = (b.usageCount ?? 0);
    if (ua !== ub) return ub - ua;
    return (a.name ?? '').localeCompare(b.name ?? '');
  });
  return candidates[0];
}

/**
 * Convenience wrapper for the auto-from-line creation path. Takes a
 * pre-computed length + endpoint annotation ids, resolves the cable
 * catalog by name, then calls the existing `createCableRun`. Returns
 * the new run's id.
 *
 * If `cableTypeName` doesn't resolve to a catalog item, the run is
 * created with `cableCatalogItemId: undefined` and a zero-priced
 * snapshot — the user can fix the type from the cable schedule's
 * inline dropdown. Mirrors `createCableRun`'s graceful-fallback
 * behaviour for that branch.
 */
export async function createCableRunFromLine(
  projectId: string,
  args: {
    planId: string;
    fromAnnotationId: string;
    toAnnotationId: string;
    lengthMeters: number;
    cableTypeName?: string;
    label: string;
    fromLocation: string;
    toLocation: string;
    installationMethod?: CableRun['installationMethod'];
    dwellingId?: string;
    notes?: string;
  },
  actor?: { id: string; name: string }
): Promise<string> {
  const catalog = args.cableTypeName
    ? await findCableCatalogByTypeName(args.cableTypeName)
    : null;
  return createCableRun(
    projectId,
    {
      label: args.label,
      fromLocation: args.fromLocation,
      fromAnnotationId: args.fromAnnotationId,
      toLocation: args.toLocation,
      toAnnotationId: args.toAnnotationId,
      planId: args.planId,
      cableCatalogItemId: catalog?.id,
      lengthMeters: args.lengthMeters,
      installationMethod: args.installationMethod ?? 'in-wall',
      status: 'Planned',
      dwellingId: args.dwellingId,
      notes: args.notes,
    } as Omit<CableRun, 'id' | 'projectId' | 'runNumber' | 'priceSnapshot' | 'createdAt' | 'updatedAt'>,
    actor
  );
}

// ---------------------------------------------------------------------------
// P5.3 — Auto-generate CableRun stubs from FieldScope zones
// ---------------------------------------------------------------------------

/** Result of a `generateScopeStubs` run. */
export interface GenerateScopeStubsResult {
  /** Stubs newly created in this run (deficit-only, not already-existing). */
  created: CableRun[];
  /** Scope items considered but skipped because they're not in
   *  `SCOPE_ITEM_MAP` (e.g. mount-pole, smart-plug — not cabled
   *  devices) OR because the existing-run count already meets the
   *  declared quantity. The number is informational so the UI can
   *  say "3 stubs created, 4 skipped (already covered)". */
  skipped: number;
  /** Names of scope items that mapped to symbols but couldn't have
   *  their default cable type resolved against the parts catalog —
   *  the stub was still created (without a cable type) and the PM
   *  needs to pick one manually via the P5.1 detail panel. Surfaces
   *  in the table's "Generate stubs" feedback toast. */
  warnings: string[];
}

/**
 * P5.3: read a dwelling's `fieldScope.zones`, spawn stub `CableRun`s
 * for each declared scope item that has a cable mapping. Idempotent
 * via deficit math — re-running when 2 of 3 stubs already exist only
 * creates the third.
 *
 * Generated stubs land with:
 *   - `status: 'Planned'`
 *   - `fromAnnotationId / toAnnotationId`: undefined (the PM links
 *     them as they drop symbols on the plan, or manually via P5.1)
 *   - `fromLocation`: the zone name (so the schedule reads
 *     something rather than blanks until linked)
 *   - `toLocation`: empty
 *   - `lengthMeters: 0` (real length lands when the PM draws the
 *     cable line on the plan — see P5.2)
 *   - `cableCatalogItemId`: resolved via the scope-item → cable-type
 *     map + `findCableCatalogByTypeName`. Missing catalog entry is
 *     a warning, not an error — stub still gets created so the PM
 *     can scan the schedule and see the gap.
 *   - `scopeItemId`: the scope item id from `EQUIPMENT_CATEGORIES`,
 *     used by P5.3's auto-link-on-symbol-placement to find matching
 *     unlinked stubs.
 *   - `label`: `{scope-item-label}-{N}` numbered per zone (e.g.
 *     "Access Point-1", "Access Point-2"). Per the master plan §4.6
 *     spec — sortable in the schedule, recognisable in the field.
 */
export async function generateScopeStubs(
  projectId: string,
  dwellingId: string,
  actor?: { id: string; name: string }
): Promise<GenerateScopeStubsResult> {
  const dwelling = await getDwelling(projectId, dwellingId);
  if (!dwelling) {
    throw new Error(`generateScopeStubs: dwelling ${dwellingId} not found`);
  }
  const zones = dwelling.fieldScope?.zones ?? [];
  if (zones.length === 0) {
    return { created: [], skipped: 0, warnings: [] };
  }

  // Load existing cable runs once so we can compute deficit per
  // (zone, scopeItemId) — see the bucket logic below. Listing all
  // runs for the project is the existing pattern.
  const existingRuns = await listCableRuns(projectId, { dwellingId });

  // Audit fix (post-P5.3): bucket existing runs by the COMPOSITE
  // (zone-name, scopeItemId) pair, not just by scopeItemId. The
  // original code used a dwelling-wide bucket which double-counted
  // when two zones declared the same scope item (e.g. "Living Room:
  // 3 APs" + "Kitchen: 2 APs" produced 3 total stubs instead of 5).
  // The zone is identified via `CableRun.fromLocation` since we
  // don't carry a proper zone-id on CableRun today (P5.3 prereq we
  // didn't take on — see CableRun type, no `zoneId` field). Using
  // `fromLocation === zone.name` is the same convention `createCableRun`
  // uses on insert, so the bucket key round-trips cleanly across
  // re-runs.
  const keyFor = (zoneName: string, scopeItemId: string) => `${zoneName}∷${scopeItemId}`;
  const existingByZoneItem = new Map<string, number>();
  for (const r of existingRuns) {
    if (!r.scopeItemId) continue;
    existingByZoneItem.set(
      keyFor(r.fromLocation ?? '', r.scopeItemId),
      (existingByZoneItem.get(keyFor(r.fromLocation ?? '', r.scopeItemId)) ?? 0) + 1
    );
  }

  const created: CableRun[] = [];
  const warnings: string[] = [];
  let skipped = 0;
  // Resolve catalog once — `findCableCatalogByTypeName` is async per
  // call so we memoise by cable-type string.
  const cableTypeCache = new Map<string, PartsCatalogItem | null>();

  for (const zone of zones) {
    const zoneName = zone.name ?? '';
    const items = zone.items ?? {};

    for (const [scopeItemId, qtyRaw] of Object.entries(items)) {
      const qty = Number(qtyRaw) || 0;
      if (qty <= 0) continue;

      const mapping = getScopeItemMapping(scopeItemId);
      if (!mapping) {
        // Scope item isn't cabled (e.g. mount-pole, smart-plug) —
        // intentional skip, no warning.
        skipped += qty;
        continue;
      }

      const bucketKey = keyFor(zoneName, scopeItemId);
      const existingInZone = existingByZoneItem.get(bucketKey) ?? 0;
      const deficit = qty - existingInZone;
      if (deficit <= 0) {
        // All declared items already covered by stubs/runs in this
        // zone — re-run is a no-op for this slot.
        skipped += qty;
        continue;
      }

      // Resolve cable type → catalog item once per scope item.
      const cableTypeHint = resolveScopeItemCableType(scopeItemId);
      let catalogItem: PartsCatalogItem | null | undefined;
      if (cableTypeHint) {
        if (!cableTypeCache.has(cableTypeHint)) {
          cableTypeCache.set(cableTypeHint, await findCableCatalogByTypeName(cableTypeHint));
        }
        catalogItem = cableTypeCache.get(cableTypeHint);
      }
      if (cableTypeHint && !catalogItem) {
        warnings.push(
          `${mapping.scopeItemLabel}: no catalog match for cable type "${cableTypeHint}"`
        );
      }

      // Sequence numbering picks up where the existing zone bucket
      // left off, so a re-run continues "AP-4, AP-5" instead of
      // restarting at 1.
      for (let i = 0; i < deficit; i += 1) {
        const seq = existingInZone + i + 1;
        const label = `${mapping.scopeItemLabel}-${seq}`;

        try {
          const runId = await createCableRun(
            projectId,
            {
              label,
              fromLocation: zoneName,
              toLocation: '',
              cableCatalogItemId: catalogItem?.id,
              lengthMeters: 0,
              installationMethod: 'in-wall',
              status: 'Planned',
              dwellingId,
              scopeItemId,
            } as Omit<CableRun, 'id' | 'projectId' | 'runNumber' | 'priceSnapshot' | 'createdAt' | 'updatedAt'>,
            actor
          );
          // Fetch back so the caller has the full run (with run
          // number + priceSnapshot) for the result list.
          const newRun = await getCableRun(projectId, runId);
          if (newRun) {
            created.push(newRun);
            // Bump THIS zone's bucket so a second iteration (deficit
            // > 1) picks up the next sequence number correctly. Other
            // zones' buckets are untouched.
            existingByZoneItem.set(bucketKey, existingInZone + i + 1);
          }
        } catch (err) {
          console.error('[generateScopeStubs] createCableRun failed:', err);
          warnings.push(
            `${mapping.scopeItemLabel}: failed to create stub (${
              err instanceof Error ? err.message : 'unknown error'
            })`
          );
        }
      }
    }
  }

  return { created, skipped, warnings };
}

/** P5.3 auto-link helper. Given a freshly-placed symbol annotation
 *  on a plan + the project's cable runs (already in memory via the
 *  parent's subscription), find the FIRST unlinked stub whose
 *  `scopeItemId` maps to this symbol's id (via `SCOPE_ITEM_MAP`),
 *  and link it. Returns the run id that was linked, or undefined
 *  when no eligible stub exists.
 *
 *  An "eligible stub" is one where:
 *    - dwellingId matches the plan's dwellingId
 *    - scopeItemId matches a scope item that this symbol satisfies
 *    - fromAnnotationId is undefined (not yet linked to anything)
 *    - status === 'Planned' (don't link a stub the PM has already
 *      promoted further down the lifecycle by hand) */
export async function autoLinkStubToSymbol(
  projectId: string,
  args: {
    annotationId: string;
    symbolId: string;
    dwellingId: string;
  }
): Promise<string | undefined> {
  // Reverse-lookup the symbol → scope-item entry. Multiple scope
  // items can share a symbol (e.g. ceiling AP + wall AP both
  // satisfy a generic "ap" scope item); we currently have a 1:1
  // map but accept both directions.
  const matchingScopeItems = SCOPE_ITEM_MAP.filter((entry) =>
    entry.symbolIds.includes(args.symbolId)
  );
  if (matchingScopeItems.length === 0) return undefined;

  // Audit fix (post-P5.3): wrap "find first unlinked stub + claim it"
  // in a Firestore transaction. Without this, the PM dropping three
  // AP symbols in quick succession can have two auto-links race —
  // both read the same snapshot, both pick the same stub, last
  // write wins, the other AP ends up with no stub linked. The
  // transaction's tx.get(runRef) inside the txn re-reads (and
  // implicitly serialises) so the second symbol's auto-link sees
  // the first one's write and picks the next available stub.
  //
  // Candidate stubs are still resolved client-side (via
  // listCableRuns) because Firestore transactions can't run queries
  // — only document gets. The strategy: list candidates, then in
  // the txn re-fetch each candidate in priority order until we
  // find one that's still unlinked, claim it, return.
  const candidates = await listCableRuns(projectId, { dwellingId: args.dwellingId });

  for (const entry of matchingScopeItems) {
    const orderedStubs = candidates.filter(
      (r) =>
        r.scopeItemId === entry.scopeItemId &&
        !r.fromAnnotationId &&
        r.status === 'Planned'
    );
    if (orderedStubs.length === 0) continue;

    const claimedRunId = await runTransaction(db, async (tx) => {
      for (const stub of orderedStubs) {
        const runRef = doc(db, 'projects', projectId, 'cableRuns', stub.id);
        const snap = await tx.get(runRef);
        if (!snap.exists()) continue; // archived/deleted between list + txn
        const live = snap.data() as Partial<CableRun>;
        // Re-verify the doc is still an eligible stub now that we
        // hold the transactional read on it. A simultaneous claim
        // by another tab will have set fromAnnotationId already.
        if (live.fromAnnotationId) continue;
        if (live.status !== 'Planned') continue;
        if (live.scopeItemId !== entry.scopeItemId) continue;
        tx.update(runRef, {
          fromAnnotationId: args.annotationId,
          updatedAt: new Date().toISOString(),
        });
        return stub.id;
      }
      return undefined;
    });

    if (claimedRunId) return claimedRunId;
  }
  return undefined;
}
